import { supabase } from '../lib/supabase';

export class AdminService {
  // Get system-wide statistics
  // Get system-wide statistics and rich analytics
  static async getSystemStats(): Promise<{ data: any | null; error: Error | null }> {
    try {
      // 1. Get conveners and admins
      const { data: conveners } = await supabase
        .from('users')
        .select('id, name, phone, role')
        .eq('is_admin', false);

      const { data: admins } = await supabase
        .from('users')
        .select('id')
        .eq('is_admin', true);

      // 2. Get total members
      const { data: members } = await supabase
        .from('members')
        .select('id, meeting_type_id');

      // 3. Get all meeting instances
      const { data: meetings } = await supabase
        .from('meeting_instances')
        .select(`
          id,
          date,
          time,
          status,
          created_by,
          meeting_type_id,
          meeting_types (
            id,
            name,
            level
          ),
          users!created_by (
            id,
            name,
            role
          )
        `)
        .order('date', { ascending: false });

      // 4. Get meetings this month
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
      
      const meetingsThisMonth = meetings?.filter(m => m.date >= firstDayOfMonth && m.date <= lastDayOfMonth) || [];
      // Completion: meetings that have attendance marked OR status = 'Completed'
      const meetingsWithAttendance = new Set<string>();

      // 5. Get attendance counts using Supabase count (avoids 1000 row limit)
      const { count: totalAttendanceCount } = await supabase
        .from('attendance_records')
        .select('*', { count: 'exact', head: true });

      const { count: totalPresentCount } = await supabase
        .from('attendance_records')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'Present');

      const totalAttendanceMarked = totalAttendanceCount || 0;
      const totalPresent = totalPresentCount || 0;
      const totalAbsent = totalAttendanceMarked - totalPresent;

      const overallAttendanceRate = totalAttendanceMarked > 0
        ? Math.round((totalPresent / totalAttendanceMarked) * 100)
        : 0;

      // Fetch all attendance records (paginated to beat 1000 limit) for per-type breakdown
      let allAttendanceRecords: any[] = [];
      let from = 0;
      const pageSize = 1000;
      while (true) {
        const { data: page } = await supabase
          .from('attendance_records')
          .select('id, meeting_instance_id, status')
          .range(from, from + pageSize - 1);
        if (!page || page.length === 0) break;
        allAttendanceRecords = allAttendanceRecords.concat(page);
        if (page.length < pageSize) break;
        from += pageSize;
      }

      // Track which meetings have attendance (= effectively completed)
      allAttendanceRecords.forEach(rec => {
        meetingsWithAttendance.add(rec.meeting_instance_id);
      });

      // Also fix status in DB for meetings that have attendance but are still "Scheduled"
      const completedMeetings = meetings?.filter(m =>
        m.status === 'Completed' || meetingsWithAttendance.has(m.id)
      ) || [];
      const scheduledMeetings = meetings?.filter(m =>
        m.status === 'Scheduled' && !meetingsWithAttendance.has(m.id)
      ) || [];

      const totalMeetingsCount = meetings?.length || 0;
      const completionRate = totalMeetingsCount > 0
        ? Math.round((completedMeetings.length / totalMeetingsCount) * 100)
        : 0;

      // 6. Get Meeting Types Breakdown
      const { data: meetingTypes } = await supabase
        .from('meeting_types')
        .select('*')
        .order('level');

      const typeStatsMap: Record<string, { meetingsCount: number; membersCount: number; presentCount: number; totalRecords: number }> = {};
      meetingTypes?.forEach(mt => {
        typeStatsMap[mt.id] = {
          meetingsCount: meetings?.filter(m => m.meeting_type_id === mt.id).length || 0,
          membersCount: members?.filter(m => m.meeting_type_id === mt.id).length || 0,
          presentCount: 0,
          totalRecords: 0
        };
      });

      const instanceTypeMap: Record<string, string> = {};
      meetings?.forEach(inst => {
        instanceTypeMap[inst.id] = inst.meeting_type_id;
      });

      allAttendanceRecords.forEach((rec: any) => {
        const typeId = instanceTypeMap[rec.meeting_instance_id];
        if (typeId && typeStatsMap[typeId]) {
          typeStatsMap[typeId].totalRecords++;
          if (rec.status === 'Present') {
            typeStatsMap[typeId].presentCount++;
          }
        }
      });

      const meetingTypeBreakdown = meetingTypes?.map(mt => {
        const tStat = typeStatsMap[mt.id] || { meetingsCount: 0, membersCount: 0, presentCount: 0, totalRecords: 0 };
        const rate = tStat.totalRecords > 0 ? Math.round((tStat.presentCount / tStat.totalRecords) * 100) : 0;
        return {
          id: mt.id,
          name: mt.name,
          level: mt.level,
          meetingsCount: tStat.meetingsCount,
          membersCount: tStat.membersCount,
          attendanceRate: rate
        };
      }) || [];

      // 7. Recent meetings with attendance calculation
      const attendanceByInstance: Record<string, { total: number; present: number }> = {};
      allAttendanceRecords.forEach((rec: any) => {
        if (!attendanceByInstance[rec.meeting_instance_id]) {
          attendanceByInstance[rec.meeting_instance_id] = { total: 0, present: 0 };
        }
        attendanceByInstance[rec.meeting_instance_id].total++;
        if (rec.status === 'Present') {
          attendanceByInstance[rec.meeting_instance_id].present++;
        }
      });

      const recentMeetings = meetings?.slice(0, 6).map(m => {
        const aStat = attendanceByInstance[m.id] || { total: 0, present: 0 };
        return {
          ...m,
          attendanceSummary: {
            total: aStat.total,
            present: aStat.present,
            rate: aStat.total > 0 ? Math.round((aStat.present / aStat.total) * 100) : 0
          }
        };
      }) || [];

      const stats = {
        totalConveners: conveners?.length || 0,
        totalAdmins: admins?.length || 0,
        totalMembers: members?.length || 0,
        totalMeetings: totalMeetingsCount,
        completedMeetings: completedMeetings.length,
        scheduledMeetings: scheduledMeetings.length,
        meetingsThisMonth: meetingsThisMonth.length,
        totalAttendanceRecords: totalAttendanceMarked,
        totalPresent,
        totalAbsent,
        overallAttendanceRate,
        completionRate,
        meetingTypeBreakdown,
        recentMeetings
      };

      return { data: stats, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Get all users
  static async getAllUsers(): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select(`
          *,
          user_meeting_assignments (
            meeting_type_id,
            meeting_types (
              id,
              name,
              level
            )
          ),
          meeting_instances!created_by (
            id,
            date,
            created_at,
            status
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Calculate last_meeting_date for each user
      const usersWithLastActivity = data?.map(user => {
        const instances = user.meeting_instances || [];
        const sorted = [...instances].sort((a: any, b: any) => {
          const dateA = a.created_at || a.date || '';
          const dateB = b.created_at || b.date || '';
          return dateB.localeCompare(dateA);
        });
        const lastMeeting = sorted[0];
        return {
          ...user,
          last_meeting_date: lastMeeting ? (lastMeeting.date || lastMeeting.created_at?.slice(0, 10)) : null,
          total_meetings_count: instances.length
        };
      });

      return { data: usersWithLastActivity, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Add new convener
  static async addConvener(userData: {
    phone: string;
    name: string;
    role: string;
    is_admin: boolean;
    meeting_type_ids: string[];
  }): Promise<{ data: any | null; error: Error | null }> {
    try {
      // Insert user
      const { data: user, error: userError } = await supabase
        .from('users')
        .insert({
          phone: userData.phone,
          name: userData.name,
          role: userData.role,
          is_admin: userData.is_admin
        })
        .select()
        .single();

      if (userError) throw userError;

      // Insert meeting assignments
      if (userData.meeting_type_ids.length > 0) {
        const assignments = userData.meeting_type_ids.map(meetingTypeId => ({
          user_id: user.id,
          meeting_type_id: meetingTypeId
        }));

        const { error: assignmentError } = await supabase
          .from('user_meeting_assignments')
          .insert(assignments);

        if (assignmentError) throw assignmentError;
      }

      return { data: user, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Update convener
  static async updateConvener(
    userId: string,
    userData: {
      name?: string;
      role?: string;
      is_admin?: boolean;
      meeting_type_ids?: string[];
    }
  ): Promise<{ data: any | null; error: Error | null }> {
    try {
      // Update user
      const { data: user, error: userError } = await supabase
        .from('users')
        .update({
          name: userData.name,
          role: userData.role,
          is_admin: userData.is_admin
        })
        .eq('id', userId)
        .select()
        .single();

      if (userError) throw userError;

      // Update meeting assignments if provided
      if (userData.meeting_type_ids !== undefined) {
        // Delete existing assignments
        await supabase
          .from('user_meeting_assignments')
          .delete()
          .eq('user_id', userId);

        // Insert new assignments
        if (userData.meeting_type_ids.length > 0) {
          const assignments = userData.meeting_type_ids.map(meetingTypeId => ({
            user_id: userId,
            meeting_type_id: meetingTypeId
          }));

          const { error: assignmentError } = await supabase
            .from('user_meeting_assignments')
            .insert(assignments);

          if (assignmentError) throw assignmentError;
        }
      }

      return { data: user, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Delete convener
  static async deleteConvener(userId: string): Promise<{ error: Error | null }> {
    try {
      // Delete meeting assignments
      await supabase
        .from('user_meeting_assignments')
        .delete()
        .eq('user_id', userId);

      // Delete user
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', userId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  // Get all members
  static async getAllMembers(): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('members')
        .select(`
          *,
          meeting_types (
            id,
            name,
            level
          )
        `)
        .order('name');

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Add member (admin can add to any type)
  static async addMember(memberData: {
    name: string;
    phone: string;
    meeting_type_id: string;
    tags?: string;
  }): Promise<{ data: any | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('members')
        .insert(memberData)
        .select(`
          *,
          meeting_types (
            id,
            name,
            level
          )
        `)
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Get all meeting instances
  static async getAllMeetings(): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('meeting_instances')
        .select(`
          *,
          meeting_types (
            id,
            name,
            level
          ),
          users (
            id,
            name,
            phone
          )
        `)
        .order('date', { ascending: false });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Update meeting status
  static async updateMeetingStatus(
    meetingId: string,
    status: 'Scheduled' | 'Completed' | 'Cancelled'
  ): Promise<{ data: any | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('meeting_instances')
        .update({ status })
        .eq('id', meetingId)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Delete meeting
  static async deleteMeeting(meetingId: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('meeting_instances')
        .delete()
        .eq('id', meetingId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  // Get attendance details for reports
  static async getAttendanceDetails(filters?: {
    meeting_type_id?: string;
    start_date?: string;
    end_date?: string;
    convener_id?: string;
  }): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      let query = supabase
        .from('attendance_records')
        .select(`
          *,
          meeting_instances!inner (
            id,
            date,
            time,
            status,
            meeting_types (
              id,
              name,
              level
            ),
            users (
              id,
              name,
              phone
            )
          ),
          members (
            id,
            name,
            phone
          )
        `);

      // Apply filters
      if (filters?.meeting_type_id) {
        query = query.eq('meeting_instances.meeting_type_id', filters.meeting_type_id);
      }
      if (filters?.start_date) {
        query = query.gte('meeting_instances.date', filters.start_date);
      }
      if (filters?.end_date) {
        query = query.lte('meeting_instances.date', filters.end_date);
      }
      if (filters?.convener_id) {
        query = query.eq('meeting_instances.created_by', filters.convener_id);
      }

      const { data, error } = await query.range(0, 9999);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Get meeting types
  static async getMeetingTypes(): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('meeting_types')
        .select('*')
        .order('name');

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Get meeting types with usage stats
  static async getMeetingTypesWithStats(): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      const { data: meetingTypes, error } = await supabase
        .from('meeting_types')
        .select('*')
        .order('name');

      if (error) throw error;

      if (!meetingTypes || meetingTypes.length === 0) {
        return { data: [], error: null };
      }

      // Load counts for each meeting type
      const typesWithStats = await Promise.all(
        meetingTypes.map(async (type) => {
          const [{ count: meetingsCount }, { count: membersCount }, { count: convenersCount }] = await Promise.all([
            supabase.from('meeting_instances').select('*', { count: 'exact', head: true }).eq('meeting_type_id', type.id),
            supabase.from('members').select('*', { count: 'exact', head: true }).eq('meeting_type_id', type.id),
            supabase.from('user_meeting_assignments').select('*', { count: 'exact', head: true }).eq('meeting_type_id', type.id)
          ]);

          return {
            ...type,
            meetingsCount: meetingsCount || 0,
            membersCount: membersCount || 0,
            convenersCount: convenersCount || 0
          };
        })
      );

      return { data: typesWithStats, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Add a new meeting type
  static async addMeetingType(typeData: {
    name: string;
    level: 'State' | 'District' | 'Unit';
  }): Promise<{ data: any | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('meeting_types')
        .insert([typeData])
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Check meeting type usage
  static async checkMeetingTypeUsage(typeId: string): Promise<{
    inUse: boolean;
    meetingsCount: number;
    membersCount: number;
    convenersCount: number;
    error: Error | null;
  }> {
    try {
      const [{ count: meetingsCount }, { count: membersCount }, { count: convenersCount }] = await Promise.all([
        supabase.from('meeting_instances').select('*', { count: 'exact', head: true }).eq('meeting_type_id', typeId),
        supabase.from('members').select('*', { count: 'exact', head: true }).eq('meeting_type_id', typeId),
        supabase.from('user_meeting_assignments').select('*', { count: 'exact', head: true }).eq('meeting_type_id', typeId)
      ]);

      const mCount = meetingsCount || 0;
      const memCount = membersCount || 0;
      const cCount = convenersCount || 0;

      return {
        inUse: mCount > 0 || memCount > 0 || cCount > 0,
        meetingsCount: mCount,
        membersCount: memCount,
        convenersCount: cCount,
        error: null
      };
    } catch (error) {
      return {
        inUse: false,
        meetingsCount: 0,
        membersCount: 0,
        convenersCount: 0,
        error: error as Error
      };
    }
  }

  // Delete a meeting type with safeguard
  static async deleteMeetingType(typeId: string): Promise<{ error: Error | null; details?: any }> {
    try {
      // Check usage first
      const usage = await this.checkMeetingTypeUsage(typeId);

      if (usage.error) throw usage.error;

      if (usage.inUse) {
        return {
          error: new Error(
            `Cannot delete: This meeting type is currently in use (${usage.meetingsCount} meetings, ${usage.membersCount} members, ${usage.convenersCount} assigned conveners).`
          ),
          details: usage
        };
      }

      const { error } = await supabase
        .from('meeting_types')
        .delete()
        .eq('id', typeId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  // Get member-wise attendance report
  static async getMemberWiseReport(filters?: {
    meeting_type_id?: string;
    start_date?: string;
    end_date?: string;
    convener_id?: string;
  }): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      let query = supabase
        .from('attendance_records')
        .select(`
          *,
          meeting_instances!inner (
            id,
            date,
            time,
            status,
            meeting_types (
              id,
              name,
              level
            ),
            users (
              id,
              name,
              phone
            )
          ),
          members (
            id,
            name,
            phone
          )
        `);

      // Apply filters
      if (filters?.meeting_type_id) {
        query = query.eq('meeting_instances.meeting_type_id', filters.meeting_type_id);
      }
      if (filters?.start_date) {
        query = query.gte('meeting_instances.date', filters.start_date);
      }
      if (filters?.end_date) {
        query = query.lte('meeting_instances.date', filters.end_date);
      }
      if (filters?.convener_id) {
        query = query.eq('meeting_instances.created_by', filters.convener_id);
      }

      const { data, error } = await query.range(0, 9999);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Get meeting-wise attendance report
  static async getMeetingWiseReport(filters?: {
    meeting_type_id?: string;
    start_date?: string;
    end_date?: string;
    convener_id?: string;
  }): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      let query = supabase
        .from('attendance_records')
        .select(`
          *,
          meeting_instances!inner (
            id,
            date,
            time,
            status,
            meeting_types (
              id,
              name,
              level
            ),
            users (
              id,
              name,
              phone
            )
          ),
          members (
            id,
            name,
            phone
          )
        `);

      // Apply filters
      if (filters?.meeting_type_id) {
        query = query.eq('meeting_instances.meeting_type_id', filters.meeting_type_id);
      }
      if (filters?.start_date) {
        query = query.gte('meeting_instances.date', filters.start_date);
      }
      if (filters?.end_date) {
        query = query.lte('meeting_instances.date', filters.end_date);
      }
      if (filters?.convener_id) {
        query = query.eq('meeting_instances.created_by', filters.convener_id);
      }

      const { data, error } = await query.range(0, 9999);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Get summary report
  static async getSummaryReport(filters?: {
    meeting_type_id?: string;
    start_date?: string;
    end_date?: string;
    convener_id?: string;
  }): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      let query = supabase
        .from('attendance_records')
        .select(`
          *,
          meeting_instances!inner (
            id,
            date,
            time,
            status,
            meeting_types (
              id,
              name,
              level
            ),
            users (
              id,
              name,
              phone
            )
          ),
          members (
            id,
            name,
            phone
          )
        `);

      // Apply filters
      if (filters?.meeting_type_id) {
        query = query.eq('meeting_instances.meeting_type_id', filters.meeting_type_id);
      }
      if (filters?.start_date) {
        query = query.gte('meeting_instances.date', filters.start_date);
      }
      if (filters?.end_date) {
        query = query.lte('meeting_instances.date', filters.end_date);
      }
      if (filters?.convener_id) {
        query = query.eq('meeting_instances.created_by', filters.convener_id);
      }

      const { data, error } = await query.range(0, 9999);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Get member search report
  static async getMemberSearchReport(filters?: {
    meeting_type_id?: string;
    start_date?: string;
    end_date?: string;
    convener_id?: string;
  }): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      let query = supabase
        .from('attendance_records')
        .select(`
          *,
          meeting_instances!inner (
            id,
            date,
            time,
            status,
            meeting_types (
              id,
              name,
              level
            ),
            users (
              id,
              name,
              phone
            )
          ),
          members (
            id,
            name,
            phone
          )
        `);

      // Apply filters
      if (filters?.meeting_type_id) {
        query = query.eq('meeting_instances.meeting_type_id', filters.meeting_type_id);
      }
      if (filters?.start_date) {
        query = query.gte('meeting_instances.date', filters.start_date);
      }
      if (filters?.end_date) {
        query = query.lte('meeting_instances.date', filters.end_date);
      }
      if (filters?.convener_id) {
        query = query.eq('meeting_instances.created_by', filters.convener_id);
      }

      const { data, error } = await query.range(0, 9999);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Debug method to check attendance records
  static async debugAttendanceRecords(): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('attendance_records')
        .select('*, count(*)')
        .limit(10);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Assign meeting to convener
  static async assignMeetingToConvener(meetingData: {
    meeting_type_id: string;
    convener_id: string;
    date: string;
    time?: string;
    status?: 'Scheduled' | 'Completed' | 'Cancelled';
  }): Promise<{ data: any | null; error: Error | null }> {
    try {
      const { meeting_type_id, convener_id, date, time, status } = meetingData;

      // Verify convener exists and has convener role
      const { data: convener, error: convenerError } = await supabase
        .from('users')
        .select('*')
        .eq('id', convener_id)
        .eq('role', 'convener')
        .single();

      if (convenerError || !convener) {
        return { data: null, error: new Error('Invalid convener or convener not found') };
      }

      // Verify meeting type exists
      const { data: meetingType, error: meetingTypeError } = await supabase
        .from('meeting_types')
        .select('*')
        .eq('id', meeting_type_id)
        .single();

      if (meetingTypeError || !meetingType) {
        return { data: null, error: new Error('Invalid meeting type or meeting type not found') };
      }

      // Check if convener is assigned to this meeting type
      const { data: assignment, error: assignmentError } = await supabase
        .from('user_meeting_assignments')
        .select('*')
        .eq('user_id', convener_id)
        .eq('meeting_type_id', meeting_type_id)
        .single();

      if (assignmentError || !assignment) {
        return { data: null, error: new Error('Convener is not assigned to this meeting type') };
      }

      // Create meeting instance
      const { data: meetingInstance, error: instanceError } = await supabase
        .from('meeting_instances')
        .insert({
          instance_id: `MTG-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
          meeting_type_id,
          created_by: convener_id,
          date,
          time: time || null,
          status: status || 'Scheduled'
        })
        .select(`
          *,
          meeting_types (
            id,
            name,
            level
          )
        `)
        .single();

      if (instanceError) {
        return { data: null, error: instanceError };
      }

      return { data: meetingInstance, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }
}
